import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { generateCatalogSlug } from '../utils/slugify';

export type ColeccionRow = {
  id: string;
  empresa_id: string;
  nombre: string;
  slug: string;
  descripcion: string | null;
  imagen_url: string | null;
  icono: string | null;
  color: string;
  activa: boolean;
  fecha_inicio: string | null;
  fecha_fin: string | null;
  orden: number;
  destacada: boolean;
  productos_count?: number;
};

type NewColeccion = {
  empresa_id: string;
  nombre: string;
  slug?: string;
  descripcion?: string;
  icono?: string;
  color?: string;
  imagen_url?: string | null;
  activa?: boolean;
  fecha_inicio?: string | null;
  fecha_fin?: string | null;
  orden?: number;
  destacada?: boolean;
};

interface ColeccionesState {
  colecciones: ColeccionRow[];
  isLoading: boolean;
  error: string | null;
  fetchColecciones: (empresaId: string) => Promise<void>;
  createColeccion: (data: NewColeccion) => Promise<ColeccionRow | null>;
  addProductosToColeccion: (coleccionId: string, productoIds: string[]) => Promise<void>;
  removeProductoFromColeccion: (coleccionId: string, productoId: string) => Promise<void>;
  setProductoColecciones: (productoId: string, coleccionIds: string[]) => Promise<void>;
  generateSlug: (nombre: string) => string;
  clear: () => void;
}

export const useColeccionesStore = create<ColeccionesState>((set, get) => ({
  colecciones: [],
  isLoading: false,
  error: null,

  generateSlug: (nombre: string) => generateCatalogSlug(nombre),

  fetchColecciones: async (empresaId: string) => {
    set({ isLoading: true, error: null });
    const { data, error } = await supabase
      .from('colecciones')
      .select('id, empresa_id, nombre, slug, descripcion, imagen_url, icono, color, activa, fecha_inicio, fecha_fin, orden, destacada, coleccion_productos(count)')
      .eq('empresa_id', empresaId)
      .order('destacada', { ascending: false })
      .order('orden', { ascending: true });

    if (error) {
      set({ error: error.message, isLoading: false, colecciones: [] });
      return;
    }
    const rows = (data || []).map((c: any) => {
      const { coleccion_productos, ...rest } = c;
      return {
        ...rest,
        productos_count: Number(coleccion_productos?.[0]?.count) || 0,
      } as ColeccionRow;
    });
    set({ colecciones: rows as ColeccionRow[], isLoading: false });
  },

  createColeccion: async (data) => {
    const slug = data.slug?.trim() ? generateCatalogSlug(data.slug) : generateCatalogSlug(data.nombre);
    const { data: created, error } = await supabase
      .from('colecciones')
      .insert({
        empresa_id: data.empresa_id,
        nombre: data.nombre.trim(),
        slug,
        descripcion: data.descripcion || null,
        icono: data.icono || null,
        color: data.color || '#1A56DB',
        imagen_url: data.imagen_url || null,
        activa: data.activa !== false,
        fecha_inicio: data.fecha_inicio || null,
        fecha_fin: data.fecha_fin || null,
        orden: data.orden ?? 0,
        destacada: data.destacada === true,
      })
      .select()
      .single();

    if (error) {
      set({ error: error.message });
      return null;
    }
    await get().fetchColecciones(data.empresa_id);
    return created as ColeccionRow;
  },

  addProductosToColeccion: async (coleccionId, productoIds) => {
    if (productoIds.length === 0) return;
    const { data: existing } = await supabase
      .from('coleccion_productos')
      .select('producto_id, orden')
      .eq('coleccion_id', coleccionId);
    const existingIds = new Set((existing || []).map((r) => r.producto_id));
    const maxOrden = (existing || []).reduce((m, r) => Math.max(m, r.orden ?? 0), -1);
    let o = maxOrden + 1;
    const toAdd = productoIds.filter((id) => !existingIds.has(id));
    if (toAdd.length === 0) return;
    const rows = toAdd.map((producto_id) => ({
      coleccion_id: coleccionId,
      producto_id,
      orden: o++,
    }));
    const { error } = await supabase.from('coleccion_productos').insert(rows);
    if (error) throw new Error(error.message);
  },

  removeProductoFromColeccion: async (coleccionId, productoId) => {
    const { error } = await supabase
      .from('coleccion_productos')
      .delete()
      .eq('coleccion_id', coleccionId)
      .eq('producto_id', productoId);
    if (error) throw new Error(error.message);
  },

  /** Sincroniza las membresías del producto: quita las que faltan y añade las nuevas. */
  setProductoColecciones: async (productoId, coleccionIds) => {
    const { data: current } = await supabase
      .from('coleccion_productos')
      .select('coleccion_id')
      .eq('producto_id', productoId);
    const have = new Set((current || []).map((r) => r.coleccion_id));
    const want = new Set(coleccionIds);
    const toRemove = Array.from(have).filter((id) => !want.has(id));
    const toAdd = Array.from(want).filter((id) => !have.has(id));
    for (const cid of toRemove) {
      await get().removeProductoFromColeccion(cid, productoId);
    }
    for (const cid of toAdd) {
      await get().addProductosToColeccion(cid, [productoId]);
    }
  },

  clear: () => set({ colecciones: [], error: null, isLoading: false }),
}));
